<script setup lang="ts">
import { StackedBarChart } from '@forgd/ui-kit/charts/stacked-bar-chart'
import type { Emission } from '#core/utils/charts'
import type { ValueFormat } from '@forgd/formatter'
import { getBarWidth } from '#core/services/unovis/helpers'
import { formatValue } from '@forgd/formatter'
import { NoData } from '@forgd/ui-kit/blocks/empty-states'
import { ColorKey, ColorKeyLabel, ColorKeyValue } from '@forgd/ui-kit/color-key'
import { useElementSize } from '@vueuse/core'
import { generateBarChartMetadata } from '#design-tokenomics/utils/chart'

const props = withDefaults(defineProps<{
  emissionSchedule?: {
    cumulativeEmission: Emission[]
  } | null
  height?: number
  yFormat?: ValueFormat
  ticker?: string | null
  // optional colors for the chart; if not supplied are generated from the data
  colors?: Record<string, string>
  gap?: number
}>(), {
  height: 200,
  yFormat: 'percent',
  gap: 2,
})

const hasData = computed(() => !!props.emissionSchedule)
const chartData = computed(() => props.emissionSchedule?.cumulativeEmission ?? [])

function sumValues(data: Record<string, number>) {
  if (!data || Object.keys(data).length === 0) {
    return 0
  }
  const { month, ...values } = data
  return Object.values(values).reduce((total, value) => total + (value || 0), 0)
}

const currentMonth = ref(chartData.value.length ? chartData.value.length - 1 : 0)

const finalTotal = computed(() => {
  const lastValue = chartData.value.at(-1)
  return lastValue
    ? Math.round(sumValues(lastValue))
    : 0
})

const currentTotal = computed(() => {
  const currentData = chartData.value[currentMonth.value] || {}
  return sumValues(currentData)
})

const currentUnlocked = computed(() => Math.round(currentTotal.value / finalTotal.value * 100))

const chartMetadata = computed(() => generateBarChartMetadata(chartData.value[chartData.value.length - 1], ['month']))
const tooltipData = computed(() => {
  const data = chartData.value.find(d => d.month === currentMonth.value)
  if (!data) {
    return []
  }
  const dataToShow: { label: string, color: string, value: number, index: number }[] = []
  for (const field in data) {
    const metaData = chartMetadata.value.fieldColors.get(field)
    if (metaData && data[field]) {
      dataToShow.push({
        ...metaData,
        value: data[field] as number,
      })
    }
  }
  return dataToShow.toSorted((a, b) => a.index - b.index)
})

const chartContainer = ref<HTMLElement | null>(null)
const { width: containerWidth } = useElementSize(chartContainer)

const chartWidth = computed(() => {
  return containerWidth.value > 0 ? containerWidth.value : 962
})

const barWidth = computed(() => {
  return getBarWidth(chartData.value, { width: chartWidth.value, gap: props.gap }, 30)
})
</script>

<template>
  <div data-el="DtEmissionSchedule" class="flex h-full flex-col px-5 py-4">
    <NoData v-if="!hasData" />
    <div v-else ref="chartContainer" class="flex grow items-center">
      <StackedBarChart
        title="Maximum Token Supply | Months Since TGE"
        :data="chartData"
        x="month"
        :y="chartMetadata.yAxisAccessors"
        :xy-container="{
          height,
        }"
        :stacked-bar="{
          color: chartMetadata.colors,
          barWidth,
        }"
        :x-axis="{
          tickThreshold: 12,
        }"
        :y-axis="{
          tickFormat: props.yFormat,
        }"
        @hover="e => currentMonth = e.index"
      >
        <template #overlay="{ d }">
          <div class="font-semibold">
            Month {{ d.month }}
          </div>
          <div class="my-1 flex justify-between">
            <span>Total unlocked:</span>
            <span class="font-semibold">{{ currentUnlocked }}%</span>
          </div>
          <div v-for="data in tooltipData" :key="data.label">
            <ColorKey :color="data.color" layout="justify" class="mt-1 tabular-nums">
              <ColorKeyLabel>{{ data.label }}</ColorKeyLabel>
              <ColorKeyValue>{{ formatValue(data.value, yFormat) }}</ColorKeyValue>
            </ColorKey>
          </div>
        </template>
      </StackedBarChart>
    </div>
  </div>
</template>
